import React from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { BiPlus } from '../../../icons';
import IconEmail_sm from '../../../images/profile-icons/icon-email.png';
import IconCall from '../../../images/profile-icons/icon-call.png';
import IconLocation_sm from '../../../images/profile-icons/icon-location.png';
import IconeUser from '../../../images/profile-icons/icon-user.png';
import IconeRelation from '../../../images/profile-icons/icon-relation.png';
import IconEdit from '../../../images/icon-edit.png';
import { formatPhoneNumber } from '../Auth/utils/phoneFormatter.js';

interface LegacyContact {
  id: string;
  name: string;
  email: string;
  phone: string;
  relationship: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
}

interface LegacyContactsSectionProps {
  legacyContacts: LegacyContact[];
  onEditLegacyContact: (contact?: LegacyContact | null) => void;
}

const LegacyContactCard: React.FC<{
  contact: LegacyContact;
  title: string;
  onEdit: () => void;
}> = ({ contact, title, onEdit }) => (
  <div
    className="p-3 rounded h-100"
    style={{ backgroundColor: "#EFF4FF" }}
  >
    <h5
      className="fw-bold mb-3"
      style={{ color: "#3957A0" }}
    >
      {title}
    </h5>
    <Button
      variant="link"
      className="edit-btn"
      onClick={onEdit}
      style={{
        position: "absolute",
        top: "10px",
        right: "10px",
      }}
    >
      <img src={IconEdit} alt="Edit Icon" />
    </Button>

    <div className="legacy-contact-details">
      <div className="contact-name mb-2">
        <img
          src={IconeUser}
          alt="Icon"
          className="detail-icon me-2"
        />
        <span
          className="fw-bold"
          style={{ fontSize: "#382F2D" }}
        >
          {contact.name}
        </span>
      </div>
      <div className="contact-relationship mb-2">
        <img
          src={IconeRelation}
          alt="Icon"
          className="detail-icon me-2"
        />
        <span
          className="fw-bold"
          style={{ fontSize: "#382F2D" }}
        >
          {contact.relationship}
        </span>
      </div>
      <div className="contact-email mb-2">
        <img
          src={IconEmail_sm}
          alt="Icon"
          className="detail-icon me-2"
        />
        <span
          className="fw-bold"
          style={{ fontSize: "#382F2D" }}
        >
          {contact.email}
        </span>
      </div>
      <div className="contact-phone mb-2">
        <img
          src={IconCall}
          alt="Icon"
          className="detail-icon me-2"
        />
        <span
          className="fw-bold"
          style={{ fontSize: "#382F2D" }}
        >
          {contact.phone ? formatPhoneNumber(contact.phone) : 'N/A'}
        </span>
      </div>
      <div className="contact-address d-flex">
        <div className="contact-icon-wrapper me-2">
          <img
            src={IconLocation_sm}
            alt="Icon"
            className="detail-icon"
          />
        </div>
        <div className="contact-text-wrapper">
          <span
            className="fw-bold"
            style={{ fontSize: "#382F2D" }}
          >
            {contact.address1},<br />
            {contact.city}, {contact.state} {contact.zip}
          </span>
        </div>
        {/* Address2 display - commented for later use
        <div className="contact-text-wrapper" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span className="fw-bold" style={{ fontSize: "#382F2D" }}>
            {contact.address1}
          </span>
          {contact.address2 && (
            <span className="fw-bold" style={{ fontSize: "#382F2D" }}>
              {contact.address2}
            </span>
          )}
          <span className="fw-bold" style={{ fontSize: "#382F2D" }}>
            {contact.city}, {contact.state} {contact.zip}
          </span>
        </div>
        */}
      </div>
    </div>
  </div>
);

const EmptyContactCard: React.FC<{
  title: string;
  onAdd: () => void;
}> = ({ title, onAdd }) => (
  <div
    className="p-3 rounded h-100 d-flex flex-column"
    style={{ backgroundColor: "#EFF4FF", minHeight: "300px" }}
  >
    <h5
      className="fw-bold mb-0"
      style={{ color: "#3957A0" }}
    >
      {title}
    </h5>
    <div className="d-flex align-items-center justify-content-center flex-grow-1">
      <Button
        variant="link"
        className="add-btn d-flex flex-column align-items-center justify-content-center"
        onClick={onAdd}
        title={`Add ${title}`}
        style={{
          color: "#D7A360",
          textDecoration: "none",
        }}
      >
        <BiPlus
          size={45}
          color="#D7A360"
          className="mb-2"
        />
        <span className="add-text text-center">
          Add {title}
        </span>
      </Button>
    </div>
  </div>
);

const LegacyContactsSection: React.FC<LegacyContactsSectionProps> = ({
  legacyContacts,
  onEditLegacyContact
}) => {
  const primaryContact = legacyContacts.find(contact => contact.id === 'primary');
  const secondaryContact = legacyContacts.find(contact => contact.id === 'secondary');

  return (
    <Row className="align-items-stretch">
      {/* Primary Legacy Contact */}
      <Col md={6} className="position-relative mb-3 mb-md-0">
        {primaryContact ? (
          <LegacyContactCard
            contact={primaryContact}
            title="Legacy Contact"
            onEdit={() => onEditLegacyContact(primaryContact)}
          />
        ) : (
          <EmptyContactCard
            title="Legacy Contact"
            onAdd={() => onEditLegacyContact()}
          />
        )}
      </Col>

      {/* Secondary Legacy Contact */}
      <Col md={6} className="position-relative">
        {secondaryContact ? (
          <LegacyContactCard
            contact={secondaryContact}
            title="Secondary Contact"
            onEdit={() => onEditLegacyContact(secondaryContact)}
          />
        ) : (
          <EmptyContactCard
            title="Secondary Contact"
            onAdd={() => onEditLegacyContact()}
          />
        )}
      </Col>
    </Row>
  );
};

export default LegacyContactsSection;